import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/authService';

export const useAdminVendorSuccessStories = (initialFilters = {}) => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    is_active: '',
    category: '',
    search: '',
    ...initialFilters,
  });

  const activeStatusOptions = [
    { value: '', label: 'All Stories' },
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
  ];

  const cleanFilters = useMemo(() => {
    return Object.fromEntries(
      Object.entries(filters).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
  }, [filters]);

  const cleanFiltersWithPagination = useMemo(() => {
    return {
      ...cleanFilters,
      per_page: cleanFilters.per_page || 10,
    };
  }, [cleanFilters]);

  const queryString = new URLSearchParams(cleanFiltersWithPagination).toString();

  const { data: storiesResponse, isLoading, error } = useQuery({
    queryKey: ['adminVendorSuccessStories', cleanFiltersWithPagination],
    queryFn: async () => {
      const response = await api.get(`/admin/vendor-success-stories${queryString ? `?${queryString}` : ''}`);
      return response.data;
    },
  });

  const { data: statisticsResponse } = useQuery({
    queryKey: ['adminVendorSuccessStoryStatistics'],
    queryFn: async () => {
      const response = await api.get('/admin/vendor-success-stories/statistics');
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/admin/vendor-success-stories', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminVendorSuccessStories']);
      queryClient.invalidateQueries(['adminVendorSuccessStoryStatistics']);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.put(`/admin/vendor-success-stories/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminVendorSuccessStories']);
      queryClient.invalidateQueries(['adminVendorSuccessStoryStatistics']);
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.patch(`/admin/vendor-success-stories/${id}/toggle-status`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminVendorSuccessStories']);
      queryClient.invalidateQueries(['adminVendorSuccessStoryStatistics']);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await api.delete(`/admin/vendor-success-stories/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['adminVendorSuccessStories']);
      queryClient.invalidateQueries(['adminVendorSuccessStoryStatistics']);
    },
  });

  const stories = useMemo(() => {
    if (storiesResponse?.data?.data) {
      return storiesResponse.data.data;
    }
    return storiesResponse?.data || [];
  }, [storiesResponse]);

  const statistics = useMemo(() => {
    return statisticsResponse?.data || {
      total: 0,
      active: 0,
      featured: 0,
    };
  }, [statisticsResponse]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return {
    stories,
    isLoading,
    error,
    filters,
    activeStatusOptions,
    handleFilterChange,
    statistics,
    createStory: (data, options) => createMutation.mutate(data, options),
    isCreating: createMutation.isPending,
    updateStory: (data, options) => updateMutation.mutate(data, options),
    isUpdating: updateMutation.isPending,
    toggleStatus: (id, options) => toggleStatusMutation.mutate(id, options),
    isToggling: toggleStatusMutation.isPending,
    deleteStory: (id, options) => deleteMutation.mutate(id, options),
    isDeleting: deleteMutation.isPending,
  };
};
